import React, {JSX, useEffect, useLayoutEffect, useState, useRef } from "react";
import { observer } from "mobx-react-lite";
import { autorun, IReactionDisposer } from "mobx";
import { ContextMenuStore } from "../../store/ContextMenuStore";
import './ContextMenu.styles.scss';

export type ContextMenuOption = {
    title: string;
    /** Optional right-aligned secondary label (e.g. shortcut or timestamp). */
    rightText?: string;
    icon?: () => JSX.Element;
    onClick?: () => void;
    /** Submenu items - if present, this option opens a submenu instead of triggering onClick */
    children?: ContextMenuOption[];
};

// This assumes <ve-portal> is either a custom element already registered, or replaced with a real portal in your app
const ContextMenu = observer(() => {
    const [position, setPosition] = React.useState(ContextMenuStore.position);
    const [options, setOptions] = React.useState(ContextMenuStore.options);
    const [config, setConfig] = React.useState(ContextMenuStore.config);
    const disposeRef = React.useRef<IReactionDisposer | null>(null);
    const [activeSubmenu, setActiveSubmenu] = useState<{ index: number; x: number; y: number } | null>(null);
    const optionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const menuRef = useRef<HTMLDivElement | null>(null);
    const submenuRef = useRef<HTMLDivElement | null>(null);

    const [clampedPosition, setClampedPosition] = useState<{ x: number; y: number } | null>(null);
    const [clampedSubmenu, setClampedSubmenu] = useState<{ x: number; y: number } | null>(null);

    useEffect(() => {
        // Sync mobx to local state
        disposeRef.current = autorun(() => {
            setPosition(ContextMenuStore.position);
            setOptions(ContextMenuStore.options);
            setConfig(ContextMenuStore.config);
            setActiveSubmenu(null); // Reset submenu when menu changes
            setClampedPosition(null);
            setClampedSubmenu(null);
        });

        // Hide on outside click
        const handleClickOutside = () => {
            ContextMenuStore.hide();
        };
        document.addEventListener("click", handleClickOutside);

        return () => {
            disposeRef.current?.();
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    const clampToViewport = (pos: { x: number; y: number }, rect: { width: number; height: number }) => {
        const margin = 8;
        const maxX = Math.max(margin, window.innerWidth - rect.width - margin);
        const maxY = Math.max(margin, window.innerHeight - rect.height - margin);
        return {
            x: Math.min(Math.max(pos.x, margin), maxX),
            y: Math.min(Math.max(pos.y, margin), maxY),
        };
    };

    // After render, clamp menu position so it never overflows the viewport (especially bottom of screen).
    useLayoutEffect(() => {
        if (!position) return;
        if (!menuRef.current) return;
        const rect = menuRef.current.getBoundingClientRect();
        const spacing = 6;
        const margin = 8;

        let desiredX = position.x;
        let desiredY = position.y;

        const anchor = config?.anchorRect;
        const placement = config?.placement;
        if (anchor && placement) {
            const placeTop = () => {
                desiredY = anchor.top - rect.height - spacing;
                if (desiredY < margin) desiredY = anchor.bottom + spacing; // flip to bottom if no space
            };
            const placeBottom = () => {
                desiredY = anchor.bottom + spacing;
                if (desiredY + rect.height + margin > window.innerHeight) {
                    desiredY = anchor.top - rect.height - spacing; // flip to top if no space
                }
            };

            switch (placement) {
                case "top-start":
                    desiredX = anchor.left;
                    placeTop();
                    break;
                case "top-end":
                    desiredX = anchor.right - rect.width;
                    placeTop();
                    break;
                case "bottom-start":
                    desiredX = anchor.left;
                    placeBottom();
                    break;
                case "bottom-end":
                    desiredX = anchor.right - rect.width;
                    placeBottom();
                    break;
            }
        }

        const next = clampToViewport({ x: desiredX, y: desiredY }, { width: rect.width, height: rect.height });
        if (!clampedPosition || clampedPosition.x !== next.x || clampedPosition.y !== next.y) {
            setClampedPosition(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        position?.x,
        position?.y,
        options.length,
        config?.placement,
        config?.anchorRect?.left,
        config?.anchorRect?.top,
        config?.anchorRect?.right,
        config?.anchorRect?.bottom,
    ]);

    const stopPropagation = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleOptionMouseEnter = (index: number, item: ContextMenuOption) => {
        if (item.children && item.children.length > 0) {
            const optionEl = optionRefs.current.get(index);
            if (optionEl) {
                const rect = optionEl.getBoundingClientRect();
                // Start with a right-opening submenu; we'll clamp/flip after render.
                setActiveSubmenu({ index, x: rect.right + 4, y: rect.top });
            }
        } else {
            setActiveSubmenu(null);
        }
    };

    const handleOptionClick = (item: ContextMenuOption) => {
        if (item.children && item.children.length > 0) {
            // Has submenu, don't trigger onClick
            return;
        }
        item.onClick?.();
    };

    const handleSubmenuItemClick = (subItem: ContextMenuOption) => {
        subItem.onClick?.();
    };

    // Clamp/flip submenu after it renders
    useLayoutEffect(() => {
        if (!activeSubmenu) {
            setClampedSubmenu(null);
            return;
        }
        if (!submenuRef.current) return;
        const rect = submenuRef.current.getBoundingClientRect();

        // Prefer the requested x/y, but if it overflows right edge, flip to left of the parent option.
        let desiredX = activeSubmenu.x;
        if (desiredX + rect.width + 8 > window.innerWidth) {
            // Find the option element so we can place submenu to its left.
            const optionEl = optionRefs.current.get(activeSubmenu.index);
            if (optionEl) {
                const optRect = optionEl.getBoundingClientRect();
                desiredX = optRect.left - rect.width - 4;
            } else {
                desiredX = window.innerWidth - rect.width - 8;
            }
        }

        const next = clampToViewport({ x: desiredX, y: activeSubmenu.y }, { width: rect.width, height: rect.height });
        if (!clampedSubmenu || clampedSubmenu.x !== next.x || clampedSubmenu.y !== next.y) {
            setClampedSubmenu(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSubmenu?.index, activeSubmenu?.x, activeSubmenu?.y, options.length]);

    if (position == null) return null;

    const { x, y } = clampedPosition ?? position;
    const style: React.CSSProperties = {
        width: config?.widthPx
            ? `min(${config.widthPx}px, calc(100vw - 16px))`
            : "205px",
        maxWidth: config?.maxWidthPx
            ? `min(${config.maxWidthPx}px, calc(100vw - 16px))`
            : undefined,
        position: "fixed",
        left: `${x}px`,
        top: `${y}px`,
    };

    return (
        <div 
            className="context-menu__content" 
            style={style} 
            onMouseDown={stopPropagation} 
            onClick={stopPropagation}
            ref={menuRef}
        >
            {options.map((item, index) => (
                <div 
                    key={"cm" + index} 
                    ref={(el) => {
                        if (el) {
                            optionRefs.current.set(index, el);
                        } else {
                            optionRefs.current.delete(index);
                        }
                    }}
                    className={`context-menu__content-option ${item.children?.length ? 'context-menu__content-option--has-submenu' : ''}`}
                    onClick={() => handleOptionClick(item)}
                    onMouseEnter={() => handleOptionMouseEnter(index, item)}
                >
                    {item.icon?.()}
                    <p className="context-menu__content-option-title">{item.title}</p>
                    {item.rightText ? (
                        <span className="context-menu__content-option-right">{item.rightText}</span>
                    ) : null}
                    {item.children && item.children.length > 0 && (
                        <span className="context-menu__content-option-arrow">›</span>
                    )}
                </div>
            ))}
            
            {/* Submenu */}
            {activeSubmenu && options[activeSubmenu.index]?.children && (
                <div 
                    className="context-menu__submenu"
                    style={{
                        position: 'fixed',
                        left: `${(clampedSubmenu?.x ?? activeSubmenu.x)}px`,
                        top: `${(clampedSubmenu?.y ?? activeSubmenu.y)}px`,
                    }}
                    onMouseDown={stopPropagation}
                    onClick={stopPropagation}
                    onMouseLeave={() => setActiveSubmenu(null)}
                    ref={submenuRef}
                >
                    {options[activeSubmenu.index].children!.map((subItem, subIndex) => (
                        <div 
                            key={"cms" + subIndex} 
                            className="context-menu__content-option"
                            onClick={() => handleSubmenuItemClick(subItem)}
                        >
                            {subItem.icon?.()}
                            <p className="context-menu__content-option-title">{subItem.title}</p>
                            {subItem.rightText ? (
                                <span className="context-menu__content-option-right">{subItem.rightText}</span>
                            ) : null}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default ContextMenu;
